import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment as config } from '../../../../environments/environment';
import { Observable, lastValueFrom } from 'rxjs';
import { ContentFilterType, ContentSearchBy, PersonalizationContentListItem, PersonalizationContent, PersonalizationContentUpdate, PersonalizationContentAdd, PersonalizationContentAddResponse, MultiplePageTagsAction } from '../models/personalization';
import { KeyValuePair, PageTag } from '../../shared/models/models';
import { IListResponse } from '../../shared/models/IListResponse';
import { BaseResponseData } from '../../shared/models/base-response';

@Injectable({
  providedIn: 'root'
})
export class PersonalizationService {
  private http = inject(HttpClient);

  getAllPagesTags(autoGenerated?: boolean | undefined): Observable<PageTag[]> {
    const params: any = {};
    if (typeof (autoGenerated) !== 'undefined' && autoGenerated != null) {
      params.autoGenerated = autoGenerated.toString();
    }

    return this.http.get<PageTag[]>(
      `${config.adminBaseUrl}/api/personalization/tags`, { params });
  }
  getPersonalizationCta(): Observable<KeyValuePair[]> {
    return this.http.get<KeyValuePair[]>(
      `${config.insightsBaseUrl}/api/personalization/cta`
    );
  }

  getContent(
    contentType: ContentFilterType, areaId: string, orderBy: string, orderDir: string, searchBy: ContentSearchBy, search: string,
    offset: number, limit: number): Observable<IListResponse<PersonalizationContentListItem>> {

    return this.http.get<IListResponse<PersonalizationContentListItem>>(
      `${config.adminBaseUrl}/api/personalization/content`, {
      params: {
        contentType: contentType.toString(),
        areaId,
        orderBy,
        direction: orderDir,
        searchBy: searchBy.toString(),
        search,
        offset: offset.toString(),
        limit: limit.toString()
      }
    });
  }

  getContentItem(contentId: number): Observable<PersonalizationContent> {
    return this.http.get<PersonalizationContent>(
      `${config.adminBaseUrl}/api/personalization/content/${contentId}`);
  }

  downloadContent(contentType: ContentFilterType, areaId: string, orderBy: string, orderDir: string, searchBy: ContentSearchBy, search: string)
    : Promise<Blob> {
    const params = new HttpParams({
      fromObject: {
        contentType: contentType.toString(),
        areaId,
        orderBy,
        direction: orderDir,
        searchBy: searchBy.toString(),
        search
      }
    });
    return lastValueFrom(this.http.get(`${config.adminBaseUrl}/api/personalization/content/download`, { params, responseType: 'blob' }));
  }

  setContentIgnore(ids: number[], isIgnore: boolean): Observable<void> {
    return this.http.patch<void>(`${config.adminBaseUrl}/api/personalization/content/set_ignore`,
      { ids, isIgnore });
  }

  setMultipleContentManualTags(contentIds: number[], tagIds: number[], action: MultiplePageTagsAction): Observable<void> {
    const body = { contentIds, tagIds, action };
    return this.http.patch<void>(`${config.adminBaseUrl}/api/personalization/content/tags`, body);
  }

  setContentManualTags(contentId: number, tagIds: number[]): Observable<void> {
    return this.http.patch<void>(`${config.adminBaseUrl}/api/personalization/content/${contentId}/tags`, tagIds);
  }

  rescanContent(ids: number[]): Observable<void> {
    return this.http.patch<void>(`${config.adminBaseUrl}/api/personalization/content/rescan`, ids);
  }

  updateContent(contentId: number, params: PersonalizationContentUpdate): Observable<BaseResponseData<PersonalizationContentAddResponse>> {
    const formData = new FormData();
    Object.keys(params).forEach(key => formData.append(key, params[key]));
    return this.http.post<BaseResponseData<PersonalizationContentAddResponse>>(`${config.adminBaseUrl}/api/personalization/content/${contentId}`, formData);
  }

  addContent(params: PersonalizationContentAdd): Observable<BaseResponseData<PersonalizationContentAddResponse>> {
    const formData = new FormData();
    Object.keys(params).forEach(key => formData.append(key, params[key]));
    return this.http.post<BaseResponseData<PersonalizationContentAddResponse>>(`${config.adminBaseUrl}/api/personalization/content/add`, formData);
  }

  ignoreImageSource(imageSource: string): Observable<void> {
    return this.http.post<void>(`${config.adminBaseUrl}/api/personalization/content/ignore_src_image`, { imageSource });
  }

  deleteManualContent(ids: number[]): Observable<void> {
    return this.http.patch<void>(`${config.adminBaseUrl}/api/personalization/content/delete`, ids);
  }
}
